import {
  useGetActivityMetricsQuery,
  useGetLocationConfigsQuery,
  useGetRecoveryConfigQuery,
  useUpdateLocationConfigsMutation,
  useUpdateRecoveryConfigMutation,
} from 'api/recoveryAi'
import type {
  LocationConfigRow,
  PilotPerformance,
  UpdateLocationConfigsBody,
} from 'api/recoveryAi.types'
import type { LocationMultiSelectLabels } from 'components/LocationMultiSelect'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectCurrentMerchantLocations } from 'selectors/location'
import { useDebouncedCallback } from 'use-debounce'
import { useAbility } from 'utilities/ability'
import type { ConfigRowSaveAdapter } from './configRowSaveAdapter'
import { locationDisplayName } from './locationDisplayName'
import type {
  LocationMode,
  LocationModePickerLabels,
} from './LocationModePicker'
import { deriveNewLocationDefault } from './newLocationDefault'

const t = buildTranslate('recovery_ai.settings.active_locations')

// Em-dash shown for the active count N until metrics load — NEVER a literal 0
// (which would falsely read as "active at no locations"). Mirrors Landing's
// ACTIVE_LOCATIONS_PLACEHOLDER.
const EM_DASH = '—'

// AI-900: how long the Settings pane's autosave waits after the LAST
// toggle/mode change before firing commit() — batches a burst of edits (e.g.
// deselecting three locations in a row) into a single PUT. Matches the
// existing search-input debounce elsewhere in RecoveryAI (Activity,
// RestrictedCustomers). Only ever armed when a host opts into `autosave`; the
// Activation modal and the onboarding wizard keep the explicit-Save flow.
export const AUTOSAVE_DEBOUNCE_MS = 300

export type ActiveLocationsControllerOptions = {
  /**
   * Skip the location-list GET until onboarding_completed.
   *
   *   context: true is the Settings pane's shipped policy — a pre-activation
   *   page view should not fire the request, and the error branch is checked
   *   before the locked branch, so a 4xx would surface "couldn't load" instead
   *   of "locked". The Activation modal must pass FALSE: it edits in exactly the
   *   pre-activation window, and the GET's real gate is `view recovery_ai`, not
   *   onboarding_completed. Passing true there renders "All 0 locations" over an
   *   empty subset — a silent wrong answer, not a crash.
   */
  skipWhenNotOnboarded: boolean
  /**
   * AI-900: opt in to debounced autosave — onToggle/onModeChange/onRemove
   * schedule commit() ~AUTOSAVE_DEBOUNCE_MS after the last edit instead of
   * waiting for an explicit Save. Defaults to false (undefined), which keeps
   * the Activation modal (ActiveLocationsConfigRow) and any other consumer on
   * the original explicit-Save flow, byte-for-byte. Only ActiveLocationsPane
   * (Settings) opts in.
   */
  autosave?: boolean
}

export type ActiveLocationsController = ConfigRowSaveAdapter & {
  mode: LocationMode
  onModeChange: (mode: LocationMode) => void
  locations: LocationConfigRow[]
  selectedIds: number[]
  onToggle: (id: number) => void
  onRemove: (id: number) => void
  selectedLocations: LocationConfigRow[]
  canManage: boolean
  /** Pre-translated strings for the i18n-agnostic picker. Memoized — see below. */
  labels: LocationModePickerLabels
  isLoading: boolean
  isFetchError: boolean
  /**
   * !onboarding_completed. The pane renders its "locked" copy; a modal host
   * IGNORES it, because that branch blanks the surface in exactly the window the
   * modal exists to make editable.
   */
  isLocked: boolean
  /** Total location count for the "All N locations" label. Undefined → em-dash. */
  totalCount?: number
  /**
   * AI-901 pilot-vs-staff performance figures. Defined ONLY when the metrics
   * endpoint returns every field, non-null (backend live); undefined otherwise,
   * so the callout renders nothing rather than a partial mix or invented numbers.
   */
  pilotPerformance?: PilotPerformance
  /**
   * The narrower dirty flag: an ACTUAL user edit, excluding defaultDirty. Only
   * this arms the route-leave guard — a legacy full-coverage merchant is
   * defaultDirty on load with no edit.
   */
  locationsDirty: boolean
  /** The location PUT landed and only the auto-enroll flag PUT failed. */
  isConfigOnlyError: boolean
  saveSuccess: boolean
  /**
   * AI-900: true while a debounced autosave is armed (the timer hasn't fired
   * yet) OR any commit() — autosaved or manually triggered — is in flight.
   * The Settings pane suppresses the unsaved-changes prompt during this
   * window (a save is imminent/underway, not abandoned) and drives its
   * "Saving…" indicator from it. Never becomes true on its own without
   * `autosave`: no host calls commit() from here without either opting into
   * autosave or wiring its own Save control (as the Activation modal does).
   */
  pendingAutosave: boolean
}

/**
 * Active-locations data layer (AI-31/AI-248): the all-vs-subset mode, the
 * hydrated selection, the derived auto-enroll flag, and the two-write save.
 * Lifted out of ActiveLocationsPane so the Settings pane and the Activation
 * modal render one implementation rather than two.
 *
 *   context: locations cannot ride RecoveryConfigDraftContext — they persist via
 *   PUT recovery/location_configs, which the config PUT does not accept — so
 *   this hook owns its own staged state and satisfies ConfigRowSaveAdapter
 *   directly. The route-leave <Prompt> deliberately stays in the pane: a modal
 *   must never mount one.
 */
export function useActiveLocationsController(
  options: ActiveLocationsControllerOptions
): ActiveLocationsController {
  const { skipWhenNotOnboarded, autosave = false } = options
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')

  const {
    data: config,
    isLoading,
    isError: isConfigFetchError,
  } = useGetRecoveryConfigQuery()
  const { data: metrics } = useGetActivityMetricsQuery()
  const {
    data: locationConfigs,
    isLoading: isLocationsLoading,
    isError: isLocationsError,
  } = useGetLocationConfigsQuery(undefined, {
    skip: skipWhenNotOnboarded && !config?.onboarding_completed,
  })
  const [
    updateLocationConfigs,
    { isLoading: isSavingLocations, isError: isSaveErrorLocations, reset },
  ] = useUpdateLocationConfigsMutation()
  const [
    updateRecoveryConfig,
    { isLoading: isSavingConfig, isError: isConfigError, reset: resetConfig },
  ] = useUpdateRecoveryConfigMutation()
  // Combined in-flight / error across the two sequential writes (the per-location
  // PUT then the config-flag PUT) so Save + the banners treat them as one op.
  const isSaving = isSavingLocations || isSavingConfig
  const isSaveError = isSaveErrorLocations || isConfigError
  // The per-location write is sequenced first, so isConfigError-without-a-
  // location-error means the selection DID persist and only the secondary
  // auto-enroll flag failed — surface copy that says so, not the generic
  // "active locations failed" (which would falsely imply the selection was lost).
  const isConfigOnlyError = isConfigError && !isSaveErrorLocations

  const [mode, setMode] = useState<LocationMode>('all')
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [saveSuccess, setSaveSuccess] = useState(false)

  // "Latest value" mirror of the mutation's in-flight flag so the hydrate
  // effect (deps: [locationConfigs]) can read it without re-running on the
  // save cycle — re-running mid-save would rehydrate from the STALE cached
  // list before the post-save invalidation refetch lands (clobbering the
  // just-submitted selection). Mirrors EscalationRulesPane.
  const isSavingRef = useRef(false)
  isSavingRef.current = isSaving

  // Hydrate the mode + selection from the saved per-location enabled state.
  // "All" only when EVERY row is enabled; otherwise "subset". A fresh, opt-in
  // merchant (all rows enabled: FALSE) therefore lands on subset/empty.
  // Guarded against an in-flight save (U5).
  useEffect(() => {
    if (!locationConfigs) return
    if (isSavingRef.current) return
    const rows = locationConfigs.data
    const enabledIds = rows.filter(r => r.enabled).map(r => r.id)
    const allEnabled = rows.length > 0 && enabledIds.length === rows.length
    // Membership compare (order-insensitive): handleToggle appends ids, so the
    // in-memory order can diverge from the server row order. Returning `prev`
    // when the SET of ids is unchanged keeps the reference stable (avoids a
    // needless rehydrate) without being fooled by a pure ordering difference.
    setSelectedIds(prev => {
      const prevSet = new Set(prev)
      const sameMembership =
        prev.length === enabledIds.length &&
        enabledIds.every(id => prevSet.has(id))
      return sameMembership ? prev : enabledIds
    })
    setMode(allEnabled ? 'all' : 'subset')
  }, [locationConfigs])

  // Clear any stale save banner / mutation error the moment the merchant edits
  // again, so a prior "Saved"/error message never lingers over fresh changes.
  // Doubles as the adapter's rollback(): this hook's staged state dies with the
  // unmount a modal close performs, so dropping the banners is all that is left.
  const clearSaveState = useCallback(() => {
    setSaveSuccess(false)
    reset()
    resetConfig()
  }, [reset, resetConfig])

  // AI-900: debounced autosave (Settings pane only — gated on the `autosave`
  // option). `commit`/`isDirty`/`isEmptySubset` are all derived further down
  // (they depend on `locations`/`enabledNow`, computed after this point), so
  // the debounced trigger reads them via a ref kept in sync on every render
  // rather than closing over them directly — a closure captured now would be
  // stale by the time the timer actually fires.
  const autosaveInputsRef = useRef({
    isDirty: false,
    isEmptySubset: false,
    canManage,
    commit: async (): Promise<boolean> => false,
  })
  // Set when an edit lands WHILE a commit is already in flight — no
  // overlapping PUTs (mirrors the disabled-while-saving Save button);
  // runCommit's `finally` below fires exactly one more commit once the
  // in-flight write settles. Also consulted (alongside isAutosaveScheduledRef)
  // by the unmount flush below.
  const pendingAutosaveRef = useRef(false)
  // True from the moment an edit arms the debounce until it either fires or
  // is skipped (invalid/pristine) — folded into `pendingAutosave` alongside
  // isSaving so the pane can suppress the unsaved-changes prompt and drive
  // its "Saving…" indicator off one signal. Mirrored into a ref (below) so the
  // unmount-only cleanup effect (empty dep array) can read its CURRENT value
  // without depending on — and therefore re-running its cleanup for — every
  // state flip.
  const [isAutosaveScheduled, setIsAutosaveScheduled] = useState(false)
  const isAutosaveScheduledRef = useRef(false)
  // Guards commit()'s post-await setState calls (setMode/setSaveSuccess) so a
  // write the unmount flush below fires never touches state after this hook's
  // owner has gone away — the mutation itself still completes independently.
  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // AI-900 (review follow-up): the REAL "a PUT is on the wire" signal, and
  // deliberately NOT the mutation hooks' isLoading. clearSaveState() runs
  // reset() on every edit; RTK Query's reset() drops the hook's tracked promise
  // (so the very next render reports isLoading:false) WITHOUT aborting the
  // request — these hooks have no fixedCacheKey, so nothing else keeps the
  // in-flight state alive either. Keying the overlap guard off isLoading
  // therefore let a second PUT go out on top of the first, with last-write-wins
  // silently deciding the merchant's selection.
  const inFlightRef = useRef(false)
  const inFlightPromiseRef = useRef<Promise<boolean> | null>(null)
  // State mirror of inFlightRef, so the pane's "Saving…" indicator and its
  // disabled Retry cover the whole real write rather than blinking off the
  // moment a concurrent reset() lands.
  const [isCommitting, setIsCommitting] = useState(false)
  // Breaks the runCommit → attemptAutosave → runCommit definition cycle:
  // runCommit drains the queue from its `finally`, but attemptAutosave is
  // defined below it (and must stay stable). Assigned every render, next to
  // autosaveInputsRef.
  const attemptAutosaveRef = useRef<() => void>(() => {})

  // The single entry point for a tracked commit: marks the write in flight for
  // its true duration, then drains at most one queued attempt once it settles.
  // commit() never rejects (it swallows its own failures and resolves false),
  // but the `finally` keeps the bookkeeping correct even if that ever changes.
  const runCommit = useCallback(async (): Promise<boolean> => {
    inFlightRef.current = true
    if (isMountedRef.current) setIsCommitting(true)
    const pending = autosaveInputsRef.current.commit()
    inFlightPromiseRef.current = pending
    try {
      return await pending
    } finally {
      inFlightRef.current = false
      inFlightPromiseRef.current = null
      if (isMountedRef.current) setIsCommitting(false)
      // Only while mounted: after an unmount the cleanup below already owns
      // the flush (and has cleared these flags), and attemptAutosave would
      // otherwise setState on a dead component.
      if (pendingAutosaveRef.current && isMountedRef.current) {
        pendingAutosaveRef.current = false
        attemptAutosaveRef.current()
      }
    }
  }, [])

  const attemptAutosave = useCallback(() => {
    setIsAutosaveScheduled(false)
    isAutosaveScheduledRef.current = false
    const {
      isDirty: dirty,
      isEmptySubset,
      canManage: manageable,
    } = autosaveInputsRef.current
    // An empty subset selection is a valid MANUAL Save (the merchant
    // explicitly clicked it), but must never autosave silently — a debounced
    // timer firing on the last deselect would persist "RecoveryAI running
    // nowhere" with no confirmation step. commit() itself is unchanged; this
    // only gates the debounced trigger. !manageable is belt-and-suspenders —
    // see the onToggle/onModeChange/onRemove call sites.
    if (!dirty || isEmptySubset || !manageable) return
    if (inFlightRef.current) {
      pendingAutosaveRef.current = true
      return
    }
    runCommit()
  }, [runCommit])
  // Kept in lockstep for the same reason as autosaveInputsRef below — a plain
  // per-render assignment, so runCommit's drain always calls THIS render's
  // attemptAutosave.
  attemptAutosaveRef.current = attemptAutosave

  const [scheduleAutosave, cancelAutosave] = useDebouncedCallback(
    attemptAutosave,
    AUTOSAVE_DEBOUNCE_MS
  )

  const triggerAutosave = useCallback(() => {
    setIsAutosaveScheduled(true)
    isAutosaveScheduledRef.current = true
    scheduleAutosave()
  }, [scheduleAutosave])

  // AI-900 (review follow-up): a merchant who edits and immediately navigates
  // away/closes the tab must not silently lose the edit — cancelling the
  // timer outright (the original behaviour) did exactly that. On unmount:
  // clear the timer, then if the LATEST staged state is still dirty and valid
  // (never an empty subset) and either a debounce was armed or a commit was
  // queued behind an in-flight one, fire commit() immediately for it. The RTK
  // mutation dispatch completes independently of this now-unmounted hook;
  // commit()'s own setState calls are guarded by isMountedRef so nothing
  // writes to unmounted state. `cancelAutosave`'s identity is stable (fixed
  // delay/deps), so this cleanup only runs on a TRUE unmount, never a
  // dependency-driven re-run.
  useEffect(() => {
    return () => {
      cancelAutosave()
      const {
        isDirty: dirty,
        isEmptySubset,
        canManage: manageable,
        commit: run,
      } = autosaveInputsRef.current
      if (
        dirty &&
        !isEmptySubset &&
        manageable &&
        (isAutosaveScheduledRef.current || pendingAutosaveRef.current)
      ) {
        // Claim the flush: clearing both flags stops runCommit's own queue
        // drain from firing a duplicate for the same edit when the
        // outstanding write settles.
        isAutosaveScheduledRef.current = false
        pendingAutosaveRef.current = false
        // Never overlap: pendingAutosaveRef being set means a commit was
        // already in flight when the debounce fired, so sequence the flush
        // BEHIND it — firing now would race the outstanding write and let the
        // older payload land last. run() is the raw commit (not runCommit):
        // there is no component left to track in-flight state for, and its
        // post-await setState calls are already isMountedRef-guarded.
        const inFlight = inFlightPromiseRef.current
        if (inFlight) {
          inFlight.then(() => {
            run()
          })
        } else {
          run()
        }
      }
    }
  }, [cancelAutosave])

  const onToggle = useCallback(
    (id: number) => {
      setSelectedIds(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
      )
      clearSaveState()
      // canManage is belt-and-suspenders: LocationModePicker already disables
      // every write affordance for a view-only operator, so this callback
      // should be unreachable without it — but a debounced autosave must
      // never fire for that role either way.
      if (autosave && canManage) triggerAutosave()
    },
    [clearSaveState, autosave, canManage, triggerAutosave]
  )

  const onRemove = useCallback(
    (id: number) => {
      setSelectedIds(prev => prev.filter(x => x !== id))
      clearSaveState()
      if (autosave && canManage) triggerAutosave()
    },
    [clearSaveState, autosave, canManage, triggerAutosave]
  )

  const onModeChange = useCallback(
    (next: LocationMode) => {
      setMode(next)
      clearSaveState()
      if (autosave && canManage) triggerAutosave()
    },
    [clearSaveState, autosave, canManage, triggerAutosave]
  )

  // AI-1173: locations are loaded app-wide nowhere near RecoveryAI, so the
  // host scene (ActiveLocationsPane) dispatches getLocations itself; this hook
  // just reads the resulting Redux-ORM cache to look up a blank-name row's
  // street address (recovery/location_configs has no address fields of its
  // own). Keyed by id — the same location id on both endpoints.
  const ormLocations = useSelector(selectCurrentMerchantLocations)
  const ormLocationsById = useMemo(
    () => new Map(ormLocations.map(location => [location.id, location])),
    [ormLocations]
  )

  // AI-1173: a location with no configured name normalizes to '' at the API
  // boundary (which has no buildTranslate access) — resolve the translated
  // "Unnamed location"/"Unnamed location (<address>)" fallback here, the first
  // point in the data flow with `t`. Both LocationMultiSelect's options and
  // SelectedLocationsList's rows read `location.name` off this
  // (selectedLocations derives from it below), so fixing it here covers both
  // i18n-agnostic presentational components.
  const locations = useMemo(
    () =>
      (locationConfigs?.data ?? []).map(location => ({
        ...location,
        name: locationDisplayName(
          location,
          ormLocationsById.get(location.id),
          t
        ),
      })),
    [locationConfigs, ormLocationsById]
  )
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedLocations = useMemo(
    () => locations.filter(l => selectedIdSet.has(l.id)),
    [locations, selectedIdSet]
  )

  // The would-be-enabled set: every location under "all", else the picked
  // subset. The single source of truth for the payload, dirty flags, and the
  // canonicalized new-location default.
  const enabledNow = useMemo(
    () => new Set(mode === 'all' ? locations.map(l => l.id) : selectedIds),
    [mode, locations, selectedIds]
  )

  // AI-248/AI-392: the merchant auto-enrolls net-new locations iff they're at
  // 100% coverage (every location enabled after Save) — derived, never a stored
  // mode enum. A full individual selection collapses to true; any deselection
  // drops it to false.
  const newLocationDefault = useMemo(
    () => deriveNewLocationDefault(locations, enabledNow),
    [locations, enabledNow]
  )
  // Persisted flag. `undefined` only when the field predates #695 in this env
  // (post-#695 the column is NOT NULL DEFAULT false + unconditionally exposed, so
  // the GET always returns it). Reconcile ONLY when the key is present: a config
  // PUT against a pre-#695 backend no-ops server-side (unpermitted param dropped
  // → 200) yet reports success and never clears defaultDirty — a false "Saved" +
  // a Save that never sticks for the whole deploy window. Presence-gating fails
  // safe pre-#695 and resumes healing (present-false → derived-true) once #695 is
  // live. (Darshan review, AI-248.)
  const persistedDefault = config?.new_location_default_enabled
  // Fail CLOSED with no location baseline: `locations` collapses to [] while the
  // GET is in flight or after it fails, which drives the derived default false —
  // so without this term a 100%-coverage merchant is dirty from SERVER data alone
  // and one Save PUTs new_location_default_enabled:false over their saved true.
  // Mirrors useEscalationContactsController's `if (!contacts) return false`; a
  // merchant with genuinely zero locations has nothing to enrol either way.
  const defaultDirty =
    locations.length > 0 &&
    persistedDefault !== undefined &&
    persistedDefault !== newLocationDefault

  // Dirty when any saved row's enabled state differs from what we'd persist.
  const locationsDirty = useMemo(
    () => locations.some(l => l.enabled !== enabledNow.has(l.id)),
    [locations, enabledNow]
  )
  // Combined: a per-location change OR a flag reconciliation. Pristine forms are
  // non-dirty, so Save can't fire a no-op PUT + false banner. defaultDirty is
  // what heals a legacy full-coverage merchant whose flag is still false.
  const isDirty = locationsDirty || defaultDirty

  const commit = useCallback(async (): Promise<boolean> => {
    const payload: UpdateLocationConfigsBody = {
      location_configs: locations.map(l => ({
        location_id: l.id,
        enabled: enabledNow.has(l.id),
      })),
    }
    try {
      // Primary intent first: the per-location write. Then reconcile the
      // merchant-level auto-enroll flag (secondary). Sequential (not Promise.all)
      // so a failed config write leaves the already-saved locations intact, and a
      // retry re-runs config-only (locationsDirty is false by then). Each guard
      // avoids a no-op PUT.
      if (locationsDirty) await updateLocationConfigs(payload).unwrap()
      if (defaultDirty)
        await updateRecoveryConfig({
          new_location_default_enabled: newLocationDefault,
        }).unwrap()
      // Reconcile the radio directly from what was just persisted rather than
      // waiting on the hydrate effect: that effect only fires off a NEW
      // locationConfigs reference and is itself guarded on isSavingRef, so a
      // manual full-coverage subset selection could land back on the "all" GET
      // while the second (config-flag) mutation was still in flight — the guard
      // would swallow that update and leave the radio stuck on "subset" until
      // the merchant navigated away and back. newLocationDefault already IS
      // "would every location be enabled after this save", i.e. exactly the
      // all-vs-subset predicate. Guarded on isMountedRef: a commit fired from
      // the unmount-flush cleanup above (AI-900) has no component left to
      // rerender for — the mutation still completes, but nothing here should
      // write to state that no longer has an owner.
      if (isMountedRef.current) {
        setMode(newLocationDefault ? 'all' : 'subset')
        setSaveSuccess(true)
      }
      return true
    } catch {
      // The failure surfaces via the isSaveError banner the host renders; swallow
      // so the rejected unwrap() doesn't bubble as an unhandled rejection.
      return false
    }
  }, [
    locations,
    enabledNow,
    locationsDirty,
    defaultDirty,
    newLocationDefault,
    updateLocationConfigs,
    updateRecoveryConfig,
  ])

  // AI-900: the autosave-only invalid state — see attemptAutosave above. Not
  // computed until here because it isn't needed until now (mode/selectedIds
  // are stable state from earlier, but this stays next to the other derived
  // dirty/valid flags for readability).
  const isEmptySubset = mode === 'subset' && selectedIds.length === 0

  // Keep the ref attemptAutosave reads in lockstep with this render's values
  // — a plain assignment (not an effect) so it's current even for a timer
  // that fires synchronously within the same tick fake timers advance.
  autosaveInputsRef.current = {
    isDirty,
    isEmptySubset,
    canManage,
    commit,
  }

  // %{count} = the merchant's TOTAL location count M — the "All locations"
  // option means all of them, NOT the enabled subset (using the active count N
  // would shrink the label to the subset size after a subset save). An em-dash
  // until metrics load. The call site picks the _one/_other form
  // (react-redux-i18n does not auto-pluralize); undefined M falls to _other
  // ("All — locations").
  const totalCount = metrics?.totalLocationsCount

  // AI-901: assemble the complete performance figure set, or undefined. Every
  // field must be present AND non-null — Grape always sends the 6 keys, but
  // each is `null` until its cohort has enough data (never absent/undefined
  // on a real response; `undefined` only covers `!metrics` above and the
  // pre-AI-901-backend case). A partial set stays undefined so the banner
  // renders nothing rather than a partial mix or invented numbers.
  const pilotPerformance = useMemo<PilotPerformance | undefined>(() => {
    if (!metrics) return undefined
    const {
      pilotLocationsCount,
      pilotResponseRate,
      pilotResponseTimeMinutes,
      pilotReturnRate30d,
      staffReturnRate30d,
      isOutperformingStaff,
    } = metrics
    if (
      pilotLocationsCount == null ||
      pilotResponseRate == null ||
      pilotResponseTimeMinutes == null ||
      pilotReturnRate30d == null ||
      staffReturnRate30d == null ||
      isOutperformingStaff == null
    ) {
      return undefined
    }
    return {
      pilotLocationsCount,
      pilotResponseRate,
      pilotResponseTimeMinutes,
      pilotReturnRate30d,
      staffReturnRate30d,
      isOutperformingStaff,
    }
  }, [metrics])

  // A hook-returned callback, so it needs a stable identity: inline it would be
  // a fresh function every render, re-rendering the picker each time.
  const removeAriaLabel = useCallback(
    (name: string) => t('remove_location_aria_label', { name }),
    []
  )

  // Memoized for the same reason: these are plain consts inside a component
  // today, which is legal there and not legal across a hook boundary.
  const labels = useMemo<LocationModePickerLabels>(() => {
    const multiSelect: LocationMultiSelectLabels = {
      selectLocations: t('select_locations'),
      locationsSelected: t(
        selectedIds.length === 1
          ? 'locations_selected_one'
          : 'locations_selected_other',
        { count: selectedIds.length }
      ),
      noLocationsAvailable: t('no_locations_available'),
      searchLocations: t('search_locations'),
      noLocationsMatch: t('no_locations_match'),
    }
    return {
      groupLabel: t('group_label'),
      allLabel: t(
        totalCount === 1 ? 'all_locations_one' : 'all_locations_other',
        { count: totalCount ?? EM_DASH }
      ),
      subsetLabel: t('subset'),
      selectedLocationsLabel: t('selected_locations_label'),
      recommendedPillLabel: t('recommended_pill'),
      multiSelect,
      removeAriaLabel,
    }
  }, [totalCount, selectedIds.length, removeAriaLabel])

  // Block on either load-bearing fetch (config gates activation; the location
  // list is the source of truth for mode + selection). Metrics is NOT
  // load-bearing — an absent N degrades to an em-dash, not a spinner.
  const isControllerLoading = isLoading || isLocationsLoading
  // A failed FETCH (config OR the location list) is a real error, not a
  // gated-empty and not the "locked" state.
  const isFetchError = isConfigFetchError || !config || isLocationsError
  const isLocked = !config?.onboarding_completed

  // AI-900: folds the armed-but-not-yet-fired debounce together with an
  // in-flight commit (whether that commit was triggered by autosave or, on a
  // host that never opted in, a manual Save) into one "something is pending"
  // signal for the Settings pane's prompt-suppression + "Saving…" indicator.
  // isCommitting is the load-bearing term: isSaving alone drops to false the
  // moment an edit's reset() lands, mid-write (see inFlightRef above).
  const pendingAutosave = isAutosaveScheduled || isSaving || isCommitting

  // Memoized to match useEscalationContactsController: both controllers land in
  // one commit and a consumer may put a whole controller in a dep array, so the
  // two must not disagree about return identity.
  return useMemo(
    () => ({
      mode,
      onModeChange,
      locations,
      selectedIds,
      onToggle,
      onRemove,
      selectedLocations,
      canManage,
      labels,
      isLoading: isControllerLoading,
      isFetchError,
      isLocked,
      totalCount,
      pilotPerformance,
      locationsDirty,
      isConfigOnlyError,
      saveSuccess,
      isDirty,
      isSaving,
      isSaveError,
      // The TRACKED commit: a manual Save/Retry has to register as in flight
      // too, or an autosave firing alongside it would overlap the write it
      // cannot see. Stable identity (runCommit reads the latest commit off
      // autosaveInputsRef), so a host may safely put it in a dep array.
      commit: runCommit,
      rollback: clearSaveState,
      pendingAutosave,
    }),
    [
      mode,
      onModeChange,
      locations,
      selectedIds,
      onToggle,
      onRemove,
      selectedLocations,
      canManage,
      labels,
      isControllerLoading,
      isFetchError,
      isLocked,
      totalCount,
      pilotPerformance,
      locationsDirty,
      isConfigOnlyError,
      saveSuccess,
      isDirty,
      isSaving,
      isSaveError,
      runCommit,
      clearSaveState,
      pendingAutosave,
    ]
  )
}
